import React, { useEffect, useState } from "react";
import { Sparkles, TrendingUp, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

/**
 * Public "Real case outcomes" social-proof rail on the landing page.
 * Pulls from GET /api/case-outcomes (admin-curated, anonymised).
 *
 * Renders nothing if 0 outcomes exist — graceful degradation while admin
 * is still building the library.
 */
export default function CaseOutcomes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/case-outcomes", { params: { limit: 12 } });
        if (alive) setItems(r.data?.items || []);
      } catch {
        if (alive) setItems([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!items.length) return null;

  return (
    <section
      className="bg-gradient-to-b from-blue-50/40 via-white to-emerald-50/40 border-y border-blue-100/60"
      data-testid="case-outcomes-section"
    >
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl py-16 sm:py-20">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full">
            <Sparkles className="w-3.5 h-3.5"/> REAL OUTCOMES
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-3">
            Indian families who found <span className="text-emerald-700">clarity</span> with NeuroOpinion
          </h2>
          <p className="text-slate-600 mt-3 leading-relaxed">
            Anonymised stories from real second opinions delivered by our NMC-verified neurosurgeons &amp; neurologists. All identifying details have been removed; clinical details are shared with patient consent.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((o) => (
            <article
              key={o.id || `${o.condition}-${o.patient_city}`}
              className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:border-blue-200 transition-all"
              data-testid={`outcome-card-${o.id || "x"}`}
            >
              <div className="flex items-center gap-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                <span>{o.patient_age_band}{o.patient_gender ? ` · ${o.patient_gender}` : ""}</span>
                <span className="w-1 h-1 rounded-full bg-slate-300"/>
                <span>{o.patient_city}</span>
              </div>

              <h3 className="font-heading text-lg font-bold text-slate-900 mt-2.5 leading-snug">
                {o.condition}
              </h3>

              {o.presenting_complaint && (
                <div className="mt-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Presented with</div>
                  <p className="text-sm text-slate-700 mt-0.5 leading-relaxed">{o.presenting_complaint}</p>
                </div>
              )}

              {o.diagnosis_change && (
                <div className="mt-3">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700 flex items-center gap-1">
                    <ArrowRight className="w-3 h-3"/> Diagnosis update
                  </div>
                  <p className="text-sm text-slate-800 mt-0.5 leading-relaxed">{o.diagnosis_change}</p>
                </div>
              )}

              {o.outcome_summary && (
                <div className="mt-3 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3"/> Outcome
                  </div>
                  <p className="text-sm text-emerald-900 font-medium mt-0.5 leading-relaxed">{o.outcome_summary}</p>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold text-slate-800">{o.doctor_name}</div>
                  {o.doctor_specialization && (
                    <div className="text-[10px] text-slate-500">{o.doctor_specialization}</div>
                  )}
                </div>
                <div className="text-[10px] text-slate-400">
                  {o.months_ago === 0 ? "this month" : o.months_ago === 1 ? "1 month ago" : `${o.months_ago} months ago`}
                </div>
              </div>
            </article>
          ))}
        </div>

        <p className="text-[11px] text-slate-400 mt-6 text-center max-w-2xl mx-auto">
          All names, faces, IDs, and unique medical-record details have been removed. Stories are shared with explicit patient consent and reviewed by our medical team before publishing.
        </p>
      </div>
    </section>
  );
}
